import { AssetItemType, NavigationType, PublicationStatus, StreetMapType } from 'Api/Contracts/Dtos/Project/Enums';
import { IRenderer, IShapeRenderer } from 'Api/Contracts/Dtos/Project/Renderer';
import { JsonObject, JsonSchemaDto } from 'Api/Contracts/Dtos/SmartObject';
import { CreativeCommonsLicense } from 'Api/Dto/CreativeCommonsLicense';
import { File as DriveFile, IFileShare, WorkerTaskState } from 'Api/Dto/Drive';
import { CoordinateSystem, ICoordinates } from 'Api/Dto/Project';
import { IIdentity } from 'Api/Dto/Shares';
import { RenderType } from 'Framework/Components/Controls/XView/Core';
import { GeoJSON, MultiPoint, Point } from 'geojson';

export interface IPanoramaLocation {
    worldCoordinates?: Point;
    localCoordinates?: Point;
}

export interface IOrderedAssetItem {
    order: number;
}

export class AssetItem {
    public constructor(type: AssetItemType = AssetItemType.Normal) {
        this.assetItemId = 0;
        this.name = null;
        this.parentId = null;
        this.type = type;
    }

    public static fromJson(json: any): AssetItem {
        let assetItem: AssetItem = new AssetItem();
        assetItem.assetItemId = json.assetItemId;
        assetItem.name = json.name;
        assetItem.parentId = json.parentId;
        assetItem.type = json.type;
        assetItem.events = json.events ?? {};

        return assetItem;
    }

    public static jsonToArray<T>(value: any): Array<T> {
        if (Array.isArray(value)) {
            let array = new Array<T>();
            for (let v of value) {
                array.push(AssetItem.jsonToObject<T>(v));
            }
            return array;
        }
        return [];
    }

    public static jsonToObject<T>(value: any): T {
        switch (value?.type) {
            case AssetItemType.StreetMap:
                return StreetMap.fromJson(value) as unknown as T;
            case AssetItemType.PanoramaItemLink:
                return PanoramaItemLink.fromJson(value) as unknown as T;
            case AssetItemType.FileMap:
                return FileMap.fromJson(value) as unknown as T;
            case AssetItemType.Navigation:
                return Navigation.fromJson(value) as unknown as T;
            case AssetItemType.PanoramaVersion:
                return AssetPanoramaVersion.fromJson(value) as unknown as T;
            case AssetItemType.Project:
                return AssetProject.fromJson(value) as unknown as T;
            case AssetItemType.Template:
                return AssetTemplate.fromJson(value) as unknown as T;
            case AssetItemType.Link:
                return AssetLink.fromJson(value) as unknown as T;
            case AssetItemType.Hotspot:
                return AssetHotspot.fromJson(value) as unknown as T;
            case AssetItemType.MapArea:
                return AssetMapArea.fromJson(value) as unknown as T;
            case AssetItemType.File:
                return AssetFile.fromJson(value) as unknown as T;
            case AssetItemType.Directory:
                return AssetDirectory.fromJson(value) as unknown as T;
            case AssetItemType.Normal:
                return AssetFile.fromJson(value) as unknown as T;
            case AssetItemType.ProjectMap:
                return ProjectMap.fromJson(value) as unknown as T;
            case AssetItemType.PanoramaGroup:
                return AssetPanoramaGroup.fromJson(value) as unknown as T;
            case AssetItemType.SmartObject:
                return SmartObjectDto.fromJson(value) as unknown as T;
            default:
                return value;
        }
    }

    public get isSystem(): boolean {
        return this._reservedNames.includes(this.name);
    }

    private readonly _reservedNames: Array<string> = [
        AssetDirectory.DefaultSmartObjectsDirectory
    ];

    public assetItemId: number;
    public name: string;
    public parentId?: number;
    public parent?: AssetDirectory;
    public type: AssetItemType;
    public events: { [key: string]: string };
}

export class AssetFile extends AssetItem {
    public constructor(type: AssetItemType = AssetItemType.File) {
        super(type);
    }

    public static fromJson(json: any): AssetFile {
        let assetFile: AssetFile = Object.assign(new AssetFile(), AssetItem.fromJson(json));
        assetFile.file = json.file;

        if (assetFile.file) {
            assetFile.file.state = {
                creationTimeUtc: new Date(),
                description: null,
                filePathGuid: assetFile.file.guid,
                taskId: 0,
                resultId: 0,
                state: WorkerTaskState.Created,
                typeOfBody: null,
                typeOfHeader: null,
                lastExecutedTimeUtc: null
            };
        }

        return assetFile;
    }

    public file: DriveFile;
}

export class AssetDirectory extends AssetFile {
    public constructor(type: AssetItemType = AssetItemType.Directory) {
        super(type);
    }

    public static fromJson(json: any): AssetDirectory {
        let assetDirectory: AssetDirectory = Object.assign(new AssetDirectory(), AssetFile.fromJson(json));
        assetDirectory.children = AssetItem.jsonToArray<AssetItem>(json.children);

        assetDirectory.children.forEach(c => {
            c.parent = assetDirectory;
        });

        return assetDirectory;
    }

    public addChildren(...children: Array<AssetItem>): void {
        this.children.push(...children);
    }

    public removeChildren(...children: Array<AssetItem>): void {
        this.children = this.children.filter(c => !children.includes(c));
    }

    public get isDefaultSmartObjectDirectory(): boolean {
        return this.name == AssetDirectory.DefaultSmartObjectsDirectory;
    }

    public static readonly DefaultSmartObjectsDirectory: string = '$AssetItems.Project.SmartObjects';

    public children: Array<AssetItem>;
}

export interface ICompassConfiguration {
    background: string,
    foreground: string,
    border: string,
    borderThickness: number,
    arcBackground: string,
    arcBorder: string,
    arcBorderThickness: number,
    baseArcBackground: string,
    baseArcBorder: string,
    baseArcBorderThickness: number
}

export class Navigation extends AssetDirectory {
    public constructor() {
        super(AssetItemType.Navigation);
    }

    public static fromJson(json: any): Navigation {
        let assetNavigation: Navigation = Object.assign(new Navigation(), AssetDirectory.fromJson(json));
        assetNavigation.navigationType = json.navigationType;
        assetNavigation.compassConfiguration = json.compassConfiguration;

        if (assetNavigation.navigationType == NavigationType.List) {
            assetNavigation.children.sort((a: PanoramaItemLink, b: PanoramaItemLink) => a.order - b.order);
        }

        assetNavigation.children.forEach(c => {
            c.parent = assetNavigation;
        });

        return assetNavigation;
    }

    public navigationType: NavigationType;
    public compassConfiguration: ICompassConfiguration;
}

export class ProjectMap extends AssetDirectory {
    public constructor(type: AssetItemType = AssetItemType.ProjectMap) {
        super(type);
    }

    public static fromJson(json: any): ProjectMap {
        let assetProjectMap: ProjectMap = Object.assign(new ProjectMap(), AssetDirectory.fromJson(json));

        assetProjectMap.children.forEach(c => {
            c.parent = assetProjectMap;
        });

        return assetProjectMap;
    }
}

export interface IMapLocation {
    itemCoordinates: MultiPoint;
    worldCoordinates?: MultiPoint;
    localCoordinates?: MultiPoint;
}

export class FileMap extends ProjectMap {
    public constructor() {
        super(AssetItemType.FileMap);
    }

    public static fromJson(json: any): FileMap {
        let assetFileMap: FileMap = Object.assign(new FileMap(), ProjectMap.fromJson(json));
        assetFileMap.isDefault = json.isDefault;
        assetFileMap.location = json.location;

        assetFileMap.children.forEach(c => {
            c.parent = assetFileMap;
        });

        return assetFileMap;
    }

    public isDefault: boolean;
    public location: IMapLocation;
}

export class StreetMap extends ProjectMap {
    public constructor() {
        super(AssetItemType.StreetMap);
    }

    public static fromJson(json: any): StreetMap {
        let assetStreetMap: StreetMap = Object.assign(new StreetMap(), ProjectMap.fromJson(json));
        assetStreetMap.coordinates = json.coordinates;
        assetStreetMap.provider = json.provider;

        assetStreetMap.children.forEach(c => {
            c.parent = assetStreetMap;
        });

        return assetStreetMap;
    }

    public coordinates: string;
    public provider: StreetMapType;
}

export class AssetProject extends AssetDirectory {
    public constructor(type: AssetItemType = AssetItemType.Project) {
        super(type);
    }

    public static fromJson(json: any): AssetProject {
        let assetLink: AssetProject = Object.assign(new AssetProject(), AssetDirectory.fromJson(json));
        assetLink.projectId = json.projectId;
        assetLink.fileShares = AssetItem.jsonToArray<IFileShare>(json.fileShares);

        assetLink.children.forEach(c => {
            c.parent = assetLink;
        });

        return assetLink;
    }

    public projectId: number;
    public project: Project;
    public fileShares: Array<IFileShare>;
}

export class AssetTemplate extends AssetProject {
    public constructor() {
        super(AssetItemType.Template);
    }

    public static fromJson(json: any): AssetTemplate {
        let assetTemplate: AssetTemplate = Object.assign(new AssetTemplate(), AssetProject.fromJson(json));
        assetTemplate.description = json.description ?? null;
        assetTemplate.project = AssetItem.jsonToObject<Project>(json.project);

        assetTemplate.children.forEach(c => {
            c.parent = assetTemplate;
        });

        return assetTemplate;
    }

    public description: string;
}

export class AssetLink extends AssetItem {
    public constructor(type: AssetItemType = AssetItemType.Link) {
        super(type);
    }

    public static fromJson(json: any): AssetLink {
        let assetLink: AssetLink = Object.assign(new AssetLink(), AssetItem.fromJson(json));
        assetLink.assetItemLinkedId = json.assetItemLinkedId ?? null;

        return assetLink;
    }

    public assetItemLinkedId?: number;
}

export class PanoramaItemLink extends AssetLink implements IOrderedAssetItem {
    public constructor() {
        super(AssetItemType.PanoramaItemLink);
    }

    public static fromJson(json: any): PanoramaItemLink {
        let assetPanoramaItemLink: PanoramaItemLink = Object.assign(new PanoramaItemLink(), AssetLink.fromJson(json));
        assetPanoramaItemLink.order = json.order;

        return assetPanoramaItemLink;
    }

    public static fromAssetPanorama(assetPanorama: AssetPanoramaGroup, parentId: number, order: number): PanoramaItemLink {
        let link = new PanoramaItemLink();
        link.name = assetPanorama.name;
        link.parentId = parentId;
        link.assetItemLinkedId = assetPanorama.assetItemId;
        link.order = order;

        return link;
    }

    public order: number;
}

export class AssetHotspot extends AssetLink {
    public constructor(type: AssetItemType = AssetItemType.Hotspot) {
        super(type);
    }

    public static fromJson(json: any): AssetHotspot {
        let assetHotspot: AssetHotspot = Object.assign(new AssetHotspot(), AssetLink.fromJson(json));
        assetHotspot.coordinateSystem = json.coordinateSystem;
        assetHotspot.coordinates = json.coordinates;
        assetHotspot.content = json.content;

        return assetHotspot;
    }

    public coordinateSystem: CoordinateSystem;
    public coordinates: Array<ICoordinates>;
    public content: IRenderer;
}

export class AssetMapArea extends AssetHotspot {
    public constructor() {
        super(AssetItemType.MapArea);
    }

    public static fromJson(json: any): AssetMapArea {
        let assetAssetMapArea: AssetMapArea = Object.assign(new AssetMapArea(), AssetHotspot.fromJson(json));
        assetAssetMapArea.areaCoordinates = JSON.stringify(json.areaCoordinates);

        return assetAssetMapArea;
    }

    public areaCoordinates: string;
    declare public content: IShapeRenderer;
}

export class AssetPanoramaVersion extends AssetDirectory implements IOrderedAssetItem {
    public constructor() {
        super(AssetItemType.PanoramaVersion);
    }

    public static fromJson(json: any): AssetPanoramaVersion {
        let assetAssetPanorama: AssetPanoramaVersion = Object.assign(new AssetPanoramaVersion(), AssetDirectory.fromJson(json));
        assetAssetPanorama.renderType = json.renderType;
        assetAssetPanorama.startOrientation = json.startOrientation;
        assetAssetPanorama.northOrientation = json.northOrientation;
        assetAssetPanorama.offset = json.offset;
        assetAssetPanorama.order = json.order;
        assetAssetPanorama.location = json.location;

        assetAssetPanorama.children.forEach(c => {
            c.parent = assetAssetPanorama;
        });

        return assetAssetPanorama;
    }

    public renderType: RenderType;
    public startOrientation: ICoordinates;
    public northOrientation: number;
    public offset: number;
    public order: number;
    public location: IPanoramaLocation;
}

export class AssetPanoramaGroup extends AssetDirectory {
    public constructor() {
        super(AssetItemType.PanoramaGroup);
    }

    static fromJson(json: any): AssetPanoramaGroup {
        let assetAssetPanoramaGroup: AssetPanoramaGroup = Object.assign(new AssetPanoramaGroup(), AssetDirectory.fromJson(json));
        assetAssetPanoramaGroup.defaultVersionId = json.defaultVersionId;
        assetAssetPanoramaGroup.hotspots = AssetItem.jsonToArray<AssetHotspot>(json.hotspots);

        assetAssetPanoramaGroup.children.forEach(c => {
            c.parent = assetAssetPanoramaGroup;
        });

        assetAssetPanoramaGroup.hotspots.forEach(h => {
            h.parent = assetAssetPanoramaGroup;
        });

        return assetAssetPanoramaGroup;
    }

    public getDefaultPanoramaVersion(currentVersion?: string): AssetPanoramaVersion {
        const panoramaVersions = this.children as Array<AssetPanoramaVersion>;

        return panoramaVersions.find(pv => pv.name == currentVersion)
            ?? panoramaVersions.find(pv => pv.assetItemId == this.defaultVersionId)
            ?? null;
    }

    public addChildren(...children: Array<AssetItem>): void {
        const panoramaVersions: AssetFile[] = children.filter(
            (c: AssetItem): c is AssetFile => c instanceof AssetFile);

        const hotspots: AssetHotspot[] = children.filter(
            (c: AssetItem): c is AssetHotspot => c instanceof AssetHotspot);

        this.children.push(...panoramaVersions);
        this.hotspots.push(...hotspots);
    }

    public removeChildren(...children: Array<AssetItem>): void {
        this.hotspots = this.hotspots.filter(h => !children.includes(h));
        this.children = this.children.filter(c => !children.includes(c));
    }

    public defaultVersionId?: number;
    public hotspots: Array<AssetHotspot>;
}

export class SmartObjectDto<T = JsonObject> extends AssetItem {
    public constructor() {
        super(AssetItemType.SmartObject);
    }

    public static fromJson(json: any): SmartObjectDto {
        const instance = Object.assign(new SmartObjectDto(), AssetItem.fromJson(json));
        instance.dataSource = json.dataSource;
        instance.displayPropertyPath = json.displayPropertyPath;
        instance.schema = json.schema;

        return instance;
    }

    public dataSource: T;
    public displayPropertyPath?: string;
    public schema?: JsonSchemaDto;
}

export class Project {
    constructor() {
        this.assetProject = new AssetProject();
        this.tags = new Array<Tag>();
    }

    public projectId: number;
    public assetProject: AssetProject;
    public thumbnail: DriveFile;
    public fileShares: Array<IFileShare>;
    public description: string;
    public license: CreativeCommonsLicense;
    public publicationStatus: PublicationStatus;
    public location: GeoJSON;
    public tags: Array<Tag>;
    public creationTimeUtc: Date;
    public viewCounter: number;
    public layout: any;
    public size: number;
    public owner: IIdentity;
}

export class Tag {
    public tagId: number;
    public name: string;
}

export class TagCounter extends Tag {
    public count: number;
}

export class ProjectLocation {
    public location: GeoJSON;
}
